import { None, Some } from '../../option/shared/option.js';
import { Err, Ok } from '../../result/shared/result.js';

/* oxlint-disable max-lines */
/**
 * Продвигает итератор на `n` элементов и возвращает новый итератор с позиции `n+1`. Если итератор закончился, то вернет
 * {@link Err} с номером последней итерации
 *
 * ```js
 * let iter = iteratorAdvanceBy([1, 2, 3, 4, 5], 2).uwnrap();
 *
 * assert.equal(iter.next().value, 3);
 *
 * iter = iteratorAdvanceBy([1, 2, 3], 3).unwrap();
 *
 * assert.ok(iter.next().done);
 *
 * iter = iteratorAdvanceBy([1, 2, 3], 4);
 *
 * assert.ok(iter.isErr());
 * assert.equal(iter.Err, 3);
 * ```
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {number} n
 * @return {Result<IterableIterator<T>, number>}
 */
function iteratorAdvanceBy(iterable, n) {
    if (n < 0) {
        return Err(n);
    }
    const iter = iterable[Symbol.iterator]();
    for (let i = 1; i <= n; i++) {
        if (iter.next().done) {
            return Err(i);
        }
    }
    return Ok(iteratorIntoIterable(iter));
}
/**
 * Возвращает элемент на `n` итерации, если он есть. Эквивалент {@link Array.at}
 *
 * ```ts
 * const item: Option<number> = iteratorNth([1, 2, 3], 2);
 *
 * assert.deepEqual(item, Some(2));
 * ```
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {number} n
 * @return {Option<T>}
 */
function iteratorNth(iterable, n) {
    const result = iteratorAdvanceBy(iterable, n).ok();
    if (result.isNone()) {
        return result;
    }
    const item = result.Some.next();
    if (item.done) {
        return None();
    }
    return Some(item.value);
}
/**
 * Пропускает `n` элементов и возвращает итератор, который идет с этой позиции
 *
 * ```js
 * const iter = iteratorSkip([1, 2, 3, 4], 2);
 *
 * assert.equal(iterator.next().value, 3);
 * assert.equal(iterator.next().value, 4);
 * ```
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {number} n
 * @return {IterableIterator<T>}
 */
function* iteratorSkip(iterable, n) {
    const result = iteratorAdvanceBy(iterable, n).ok();
    if (result.isNone()) {
        return;
    }
    for (const item of iteratorIntoIterable(result.Some)) {
        yield item;
    }
}
/**
 * Берет первые до `n` элементов и возвращает их
 *
 * ```js
 * const iter = iteratorTake([1, 2, 3, 4], 2);
 *
 * assert.equal(iterator.next().value, 1);
 * assert.equal(iterator.next().value, 2);
 * ```
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {number} n
 * @return {IterableIterator<T>}
 */
function* iteratorTake(iterable, n) {
    let i = 0;
    for (const item of iterable) {
        if (i <= n - 1) {
            yield item;
            i++;
        }
        else {
            return;
        }
    }
}
/**
 * Проходит по всему итератору и применяет к каждому значению функцию `fn`. Аналог {@link Array.forEach}
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {(item: T) => void} fn
 */
function iteratorForEach(iterable, fn) {
    for (const item of iterable) {
        fn(item);
    }
}
/**
 * Аналог {@linkcode Array.map} и упрощенная реализация
 * [Iterator.map](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Iterator/map)
 * без индексов
 *
 * @example
 * ```ts
 * const mapper = (item: number) => item.toString();
 *
 * const arr: number[] = [1,2,2,3,3,5,5];
 * const unque: Set<number> = new Set(arr); // [1,2,3,5]
 * const map: Map<number, number>(iteratorMap(arr, (i, idx) => [idx, i])); // [0, 1], [1, 2] ...
 *
 * function print(iter: Iterable<string>) {
 *  for (const item of iter) {
 *    console.log(item);
 *  }
 * }
 *
 * print(iteratorMap(arr, mapper)); // > 1,2,2,3,3,5,5
 * print(iteratorMap(unque, mapper)); // > 1,2,3,5
 * print(iteratorMap(map.values(), mapper)); // > 1,2,2,3,3,5,5
 * ```
 *
 * @export
 * @template T
 * @template R
 * @param {Iterable<T>} iterable
 * @param {(item: T) => R} fn
 * @return {IterableIterator<R>}
 */
function* iteratorMap(iterable, fn) {
    for (const item of iterable) {
        yield fn(item);
    }
}
/**
 * Аналог {@linkcode Array.filter} и упрощенная реализация
 * [Iterator.filter](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Iterator/filter)
 * без индексов
 *
 * @example
 * ```ts
 * iteratorFilter([1,2,3], i => i > 1) // [2,3];
 * iteratorFilter(
 *   new Map([['A', 1], ['B', 2], ['C', 3]]),
 *  ([letter, i]) => i > 1,
 * ) // { B: 2, C: 3 }
 * ```
 *
 * @export
 * @template T
 * @template S
 * @param {Iterable<T>} iterable
 * @param {(item: T) => item is S} fn
 * @return {IterableIterator<S>}
 */
function* iteratorFilter(iterable, fn) {
    for (const item of iterable) {
        if (fn(item)) {
            yield item;
        }
    }
}
/**
 * Метод объединяет в себе {@linkcode iteratorMap} и {@linkcode iteratorFilter},
 * аналог из rust [Iterator.filter_map](https://doc.rust-lang.org/std/iter/trait.Iterator.html#method.filter_map)
 *
 * @example
 * ```ts
 * iteratorFilterMap([true, false, true, false], (item) => !item ? Some(item) : None()); // [false, false];
 *
 * iteratorFilterMap(
 *  new Map([
 *    ['J', { age: 18 }],
 *    ['A', { age: 42 }],
 *    ['T', { age: 31 }],
 *    ['L', { age: 24 }],
 *  ]),
 *  ([name, { age }]) => age > 25 ? Some(name) : None(),
 * ); // > ['A', 'T'];
 * ```
 *
 * @export
 * @template T
 * @template R
 * @param {Iterable<T>} iterable
 * @param {(item: T) => Option<R>} fn
 * @return {IterableIterator<R>}
 */
function* iteratorFilterMap(iterable, fn) {
    for (const item of iterable) {
        const result = fn(item);
        if (result.isSome()) {
            yield result.Some;
        }
    }
}
/**
 * Метод схож с {@linkcode iteratorMap}, он работает с коллекцией итераторов и склеивает их в единый итератор
 * Аналог [Iterator.flatMap](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Iterator/flatMap)
 *
 * @example
 * ```ts
 * iteratorFlatMap([[1, 2, 3], [5, 6, 1, 2]], (iter: Iterable<number>) => iteratorFilter(iter, i => i > 1));
 * // [2, 3, 4, 6, 2];
 *
 * new Map(
 *  iteratorFlatMap([
 *    new Map(['A', 1]),
 *    new Map(['B', 2])
 *  ], item => item)
 * );
 * // > { A: 1, B: 2 }
 * ```
 *
 * @export
 * @template T
 * @template R
 * @param {Iterable<T>} iterable
 * @param {(item: T) => Iterable<R>} fn
 * @return {IterableIterator<R>}
 */
function* iteratorFlatMap(iterable, fn) {
    for (const item of iterable) {
        yield* fn(item);
    }
}
/**
 * Склеивает вложенные итераторы в один итератор
 *
 * @export
 * @template T
 * @param {Iterable<Iterable<T>>} iterable
 * @return {IterableIterator<T>}
 */
function* iteratorFlatten(iterable) {
    for (const item of iterable) {
        yield* item;
    }
}
/**
 * Выполняет поиск значения в итераторе. Применяет к каждому элементу `predicate`
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {(item: T) => boolean} predicate
 * @return {Option<T>}
 */
function iteratorFind(iterable, predicate) {
    for (const item of iterable) {
        if (predicate(item)) {
            return Some(item);
        }
    }
    return None();
}
function* iteratorChain(self, ...other) {
    for (const item of iteratorFlatMap([self, iteratorFlatMap(other, chainMapper)], chainMapper)) {
        yield item;
    }
}
function chainMapper(item) {
    return item;
}
/**
 * Аналог {@linkcode Array.reduce}
 *
 * @export
 * @template T
 * @template B
 * @param {Iterable<T>} iterable
 * @param {(acc: B, current: T) => B} fn
 * @param {B} [initial]
 * @return {Option<B>}
 */
function iteratorReduce(iterable, fn, initial) {
    let acc = initial;
    let stateInit = arguments.length === 3;
    let itersCount = 0;
    for (const current of iterable) {
        itersCount++;
        if (!stateInit) {
            acc = current;
            stateInit = true;
            continue;
        }
        acc = fn(acc, current);
    }
    if (itersCount === 0) {
        return None();
    }
    return Some(acc);
}
var Ordering;
(function (Ordering) {
    Ordering[Ordering["Less"] = -1] = "Less";
    Ordering[Ordering["Equal"] = 0] = "Equal";
    Ordering[Ordering["Greater"] = 1] = "Greater";
})(Ordering || (Ordering = {}));
const Ord = {
    numberToOrd(x, y) {
        const result = x - y;
        if (result < 0) {
            return Ordering.Less;
        }
        else if (result > 0) {
            return Ordering.Greater;
        }
        return Ordering.Equal;
    },
};
/**
 * Возвращает максимальное значение из итератора
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {(x: T, y: T) => Ordering} compare
 * @param {boolean} [firstOnEqual=false] - по умолчанию, если два значения равны, то компаратор выберет второе значение
 * @return {Option<T>}
 */
function iteratorMaxBy(iterable, compare, firstOnEqual = false) {
    return iteratorReduce(iterable, (x, y) => maxByComporator(x, y, compare, firstOnEqual));
}
function maxByComporator(x, y, compare, firstOnEqual) {
    switch (compare(x, y)) {
        case Ordering.Greater:
            return x;
        case Ordering.Less:
            return y;
        case Ordering.Equal:
            return firstOnEqual ? x : y;
    }
}
/**
 * Возвращает минимальное значение итератора
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterable
 * @param {(x: T, y: T) => Ordering} compare
 * @param {boolean} [secondOnEqual=false] - по умолчанию, если два значения равны, то компаратор выберет первое значение
 * @return {Option<T>}
 */
function iteratorMinBy(iterable, compare, secondOnEqual = false) {
    return iteratorReduce(iterable, (x, y) => minByComporator(x, y, compare, secondOnEqual));
}
function minByComporator(x, y, compare, secondOnEqual) {
    switch (compare(x, y)) {
        case Ordering.Equal:
            return secondOnEqual ? y : x;
        case Ordering.Less:
            return x;
        case Ordering.Greater:
            return y;
    }
}
/**
 * Проверяет, является ли объект итерируемым
 *
 * @export
 * @template T
 * @param {(unknown | Iterable<T>)} val
 * @return {val is Iterable<T>}
 */
function isIterable(val) {
    if (val === null || typeof val !== 'object') {
        return false;
    }
    return Symbol.iterator in val;
}
/**
 * Возвращает итератор по свойствам объекта
 *
 * ```ts
 * const obj = { a: 1, b: '2', c: true };
 *
 * const iter = iteratorRecord(obj);
 *
 * type Entry = IterableIterator<['a', number] | ['b', string] | ['c', boolean]>;
 *
 * assert.deepEqaul(iter.next().value, ['a', number]);
 * assert.deepEqaul(iter.next().value, ['b', string]);
 * assert.deepEqaul(iter.next().value, ['c', boolean]);
 * ```
 *
 * Под капотом используется оператор `for in` и выполняется проверка через {@link Object.hasOwnProperty}
 *
 * @export
 * @template R
 * @param {R} record
 * @param {IntoIteratorRecordOpts} [opts={}]
 * @return {RecordIterator<R>}
 */
function* iteratorRecord(record, opts = {}) {
    const { functionsAsValue = false, undefinedAsValue = false } = opts;
    for (const key in record) {
        if (!Object.prototype.hasOwnProperty.call(record, key)) {
            continue;
        }
        const val = record[key];
        if (typeof val === 'function') {
            if (!functionsAsValue) {
                continue;
            }
        }
        if (val === undefined) {
            if (!undefinedAsValue) {
                continue;
            }
        }
        yield [key, val];
    }
}
/**
 * Утилита преобразует неитерируемые объекты в итерируемые. Полезно в случае union типов
 *
 * ```ts
 * type THTTPHeaders = Record<string, HeaderValue> | Iterable<[string, HeaderValue]>;
 *
 * const headers: THTTPHeaders = { 'x-foo': 'bar' };
 * const iterableHeaders = new Headers({ 'x-foo': 'bar' });
 *
 * for (const header of intoIterable(headers)) {}
 * for (const header of intoIterable(iterableHeaders)) {}
 * ```
 *
 * @export
 * @param {(Iterable<unknown> | Record<string | number, unknown>)} val
 * @return {Iterable<unknown>}
 */
function intoIterable(val) {
    if (isIterable(val)) {
        return val;
    }
    return iteratorRecord(val);
}
/**
 * Трансформирует {@link Iterator} в {@link IterableIterator}, что позволяет его использовать в
 * цикле `for .. of`
 *
 * ```ts
 * const iterator: Iterator<number> = {
 *  next() {
 *    const value = Math.random();
 *
 *    return value > 0.5 ? { done: false, value } : { done: true, value: undefined };
 *  }
 * }
 *
 * for (const val of iteratorIntoIterable(iterator)) {
 *   console.info(val);
 * }
 * ```
 *
 * @export
 * @template T
 * @param {Iterator<T>} iterator
 * @return {IterableIterator<T>}
 */
function* iteratorIntoIterable(iterator) {
    let val;
    while (!(val = iterator.next()).done) {
        yield val.value;
    }
    return;
}
/**
 * Разбивает итератор на чанки размером `chunkSize`
 *
 * ```js
 * const text = 'lorem ipsum';
 *
 * assert.deepEqual(
 *   Array.from(iteratorMap(iteratorChunks(text, 2), chunk => chunk.join(''))),
 *   ['lo', 're', 'm ', 'ip', 'su', 'm']
 * );
 * ```
 *
 * @export
 * @template T
 * @param {Iterable<T>} iterator
 * @param {number} chunkSize
 * @return {IterableIterator<T[]>}
 */
function* iteratorChunks(iterator, chunkSize) {
    let current = [];
    for (const item of iterator) {
        current.push(item);
        if (current.length === chunkSize) {
            yield current;
            current = [];
        }
    }
    if (current.length) {
        yield current;
    }
}

export { Ord, Ordering, intoIterable, isIterable, iteratorAdvanceBy, iteratorChain, iteratorChunks, iteratorFilter, iteratorFilterMap, iteratorFind, iteratorFlatMap, iteratorFlatten, iteratorForEach, iteratorIntoIterable, iteratorMap, iteratorMaxBy, iteratorMinBy, iteratorNth, iteratorRecord, iteratorReduce, iteratorSkip, iteratorTake };
//# sourceMappingURL=iterator.js.map
